import { Store, MapPin, HandCoins, Route, ArrowUpRight, Mail } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, SectionHead } from "@/components/Motion";
import VideoFeature from "@/components/VideoFeature";
import CTA from "@/components/CTA";
import { IMAGES } from "@/lib/site";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

const FEATURES = [
  {
    icon: Store,
    title: "Your own partner profile",
    body: "You get a fully branded home inside the VeloPaths app — your name, your territory, your story, your link.",
  },
  {
    icon: MapPin,
    title: "Lease zones nationwide",
    body: "You're not limited to your physical location. A partner in Rhode Island can lease zones in Georgia, Florida, California — anywhere in the US.",
  },
  {
    icon: HandCoins,
    title: "50/50 revenue share",
    body: "Add advertisers within your leased territories and earn half of all the revenue they generate, recurring every year.",
  },
  {
    icon: Route,
    title: "A curated rider experience",
    body: "Curate stops and approve advertisers on your routes — every pin riders see is one you chose to stand behind.",
  },
];

const STEPS = [
  { n: "01", title: "Apply", body: "Apply to become a VeloPaths Partner — tell us about your local cycling knowledge and service area." },
  { n: "02", title: "Get reviewed & approved", body: "We review every application personally. Approved partners are activated and enrolled in the program." },
  { n: "03", title: "Pay the one-time setup fee", body: "The $99 Founding Partner setup covers enrollment, activation, your first zone, and your first full year of participation." },
  { n: "04", title: "Browse & request zones", body: "Preview available territories across the country and request the zones you want — near home or nationwide. Your first zone is already included." },
  { n: "05", title: "VeloPaths assigns your zones", body: "Zones are assigned by VeloPaths Admin after approval. Not an instant checkout — a curated match." },
  { n: "06", title: "Manage advertisers & earn", body: "Approve local advertisers onto your routes and earn 50% of everything they spend, year after year." },
];

const FAQS = [
  {
    q: "Do I need to own a bike shop?",
    a: "No. VeloPaths welcomes both Business Partners and Trail Partners. Bike shops, rental operators, cycling clubs, trail advocates, nonprofits, tourism organizations, and experienced local cyclists may apply.",
  },
  {
    q: "What is a Trail Partner?",
    a: "A Trail Partner is an individual or organization with strong local cycling knowledge who helps manage bike paths, routes, stops, and advertiser relationships within a designated territory.",
  },
  {
    q: "What is included in the $99 Founding Partner setup?",
    a: "Enrollment and activation in the partner program, your first zone, and one full year of participation. It's a one-time fee — paid once, not annually.",
  },
  {
    q: "What is a zone?",
    a: "A zone is a leasable cycling territory — a mapped area of bike paths and destinations where your approved advertisers appear as curated stops in the VeloPaths Rider app.",
  },
  {
    q: "Can I purchase additional zones?",
    a: "Yes. Additional zones can be added at any time for an additional annual fee per zone. Pricing varies based on traffic and revenue potential, and zones are assigned by VeloPaths Admin after approval — it's a curated process, not an instant checkout.",
  },
  {
    q: "How much does advertiser participation cost?",
    a: "Founding Advertiser pricing is $99 per year per location — including pin placement within an eligible zone and full listing visibility in the Rider app.",
  },
  {
    q: "Will pricing ever change?",
    a: "Founding Partner and Founding Advertiser pricing is available for a limited time. Renewal pricing may differ in future years as the platform expands and additional features are introduced.",
  },
  {
    q: "What is founding pricing?",
    a: "Founding pricing is our preferred early-adopter rate during the initial growth phase of the VeloPaths platform — the lowest price these programs will ever be offered at, reserved for the partners and advertisers that join first.",
  },
  {
    q: "What if the area I want isn't listed?",
    a: "Submit an area request — tell us where you'd like to operate — and the VeloPaths team will follow up with you by email.",
    mail: true,
  },
  {
    q: "Do I have to lease zones where I'm located?",
    a: "Not at all. Partners can lease zones anywhere in the US. A partner in Rhode Island can operate routes in Georgia, Florida, California — wherever you see opportunity.",
  },
];

export default function RentalShops() {
  return (
    <main data-testid="page-rental-shops">
      <PageHero
        testid="rental-shops-hero"
        image={IMAGES.heroRental}
        overline="For partners"
        lines={["Build Better Cycling Experiences.", "Earn Revenue From Your Local Knowledge."]}
        sub="Help riders discover the best routes, stops, and local businesses while earning revenue from advertisers in your cycling territory."
      />

      <VideoFeature
        testid="section-video"
        overline="Watch"
        title="The Partner Program, explained."
        sub="Ninety seconds on how Velopaths Partners lease zones, curate cycling experiences, and earn a 50/50 revenue share from local advertisers."
        src="/videos/velopaths-partners-web.mp4"
        poster="/videos/velopaths-partners-poster.jpg"
        posterAlt="VeloPaths partner program video"
        badge="Partner Program · 1.5 min"
      />

      <section
        data-testid="section-partner-program"
        className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16"
      >
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2
                data-testid="partner-program-heading"
                className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#0B2B22]"
              >
                Build Better Cycling Experiences — And Earn Revenue Doing It
              </h2>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#52635B]">
                Whether you're a bike shop, cycling club, trail advocate, or local cycling
                expert, VeloPaths gives you the tools to manage bike paths, routes, stops, and
                local business recommendations within your territory.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#52635B]">
                As riders explore your content, approved local advertisers can promote their
                businesses, creating shared revenue opportunities.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="mt-9 flex flex-wrap gap-3">
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-partner-with-velopaths"
                className="inline-flex items-center gap-2 rounded-full bg-[#E05A47] px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c94a39]"
              >
                Claim Your First Zone <ArrowUpRight size={15} />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="rounded-[2rem] bg-[#0B2B22] p-8 text-[#F4F7F4] sm:p-10">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
                Founding Partner
              </p>
              <p className="mt-3">
                <span className="font-mono text-5xl font-bold">$99</span>
                <span className="text-[#F4F7F4]/60"> one-time setup</span>
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#F4F7F4]/70">
                Covers enrollment and activation — and includes your first zone plus one full year
                of participation. Paid once, not annually.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[#F4F7F4]/70">
                Founding Partner pricing is available for a limited time. Early adopters receive
                preferred pricing during the initial growth phase of the VeloPaths platform.
              </p>
              <div className="mt-8 border-t border-[#F4F7F4]/15 pt-6">
                <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
                  Additional zones
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#F4F7F4]/70">
                  Add territories anytime for an additional annual fee per zone. Zone pricing
                  varies based on traffic and revenue potential — preview each zone's yearly price
                  in the portal before you request it.
                </p>
              </div>
              <div className="mt-8 border-t border-[#F4F7F4]/15 pt-6">
                <p className="font-mono text-3xl font-bold text-[#E05A47]">
                  50/50{" "}
                  <span className="font-mono text-base font-bold text-[#F4F7F4]/70">
                    revenue share
                  </span>
                </p>
                <p className="mt-2.5 text-sm leading-relaxed text-[#F4F7F4]/70">
                  You earn 50% of all advertiser revenue generated within your leased zones. A
                  single Founding Advertiser at $99/year returns{" "}
                  <span className="font-mono font-bold text-[#7FB998]">$49.50</span> to you — two
                  advertisers cover your entire $99 setup fee.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 sm:px-10 sm:pb-32 lg:px-16">
        <SectionHead
          overline="The partner program"
          title="Your Local Knowledge Creates Value. Share In It."
          sub="Local cycling partners are the heartbeat of every trail town. Whether you're a bike shop, cycling club, advocate, or experienced rider, your local knowledge helps riders discover better experiences."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.1} className="h-full">
              <div className="group h-full rounded-[1.75rem] border border-[#154234]/10 bg-white p-8 transition-transform duration-500 hover:-translate-y-1.5">
                <span className="grid size-12 place-items-center rounded-2xl bg-[#0B2B22] text-[#7FB998] transition-colors duration-300 group-hover:bg-[#E05A47] group-hover:text-white">
                  <f.icon size={21} />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-[#0B2B22]">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#52635B]">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0B2B22] py-24 text-[#F4F7F4] sm:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHead
                dark
                overline="How onboarding works"
                title="From application to earning in six steps."
              />
              <div className="mt-12 space-y-0">
                {STEPS.map((s, i) => (
                  <Reveal key={s.n} delay={i * 0.08}>
                    <div className="flex gap-6 border-t border-[#F4F7F4]/12 py-6 last:border-b">
                      <p className="font-mono text-sm font-bold text-[#E05A47]">{s.n}</p>
                      <div>
                        <h3 className="font-display text-lg font-bold tracking-tight">{s.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-[#F4F7F4]/65">{s.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.2} className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -inset-3 translate-x-5 translate-y-5 rounded-[2rem] bg-[#E05A47]/20" />
                <img
                  src={IMAGES.rentalWorkshop}
                  alt="A rider being fitted for a rental bike at a local shop counter"
                  loading="lazy"
                  decoding="async"
                  className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-2xl"
                />
                <div className="absolute bottom-5 left-5 right-5 rounded-xl bg-[#0B2B22]/85 px-4 py-3 backdrop-blur">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#7FB998]">
                    Partner profile preview
                  </p>
                  <p className="font-display text-base font-bold text-[#F4F7F4]">
                    Riverside Cycle Co. — 12 curated stops · 3 zones
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section
        data-testid="section-partner-faq"
        className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16"
      >
        <SectionHead
          overline="Partner FAQ"
          title="Straight answers before you apply."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.06} className="h-full">
              <div className="h-full rounded-[1.75rem] border border-[#154234]/10 bg-white p-7">
                <h3 className="font-display text-lg font-bold tracking-tight text-[#0B2B22]">
                  {f.q}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#52635B]">{f.a}</p>
                {f.mail && (
                  <a
                    href="mailto:info@velopaths.com?subject=Zone%20area%20request"
                    data-testid="btn-area-request"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0B2B22] px-5 py-3 text-xs font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
                  >
                    <Mail size={13} /> Request an area
                  </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTA
        overline="Partner with VeloPaths"
        title="Ready to claim your first zone?"
        sub="Founding Partner pricing is available for a limited time — a $99 one-time setup includes your first zone and your first year."
        primary={{ label: "Become a Founding Partner", to: PORTAL_URL, testid: "btn-cta-partner" }}
        secondary={{ label: "See full pricing", to: "/pricing", testid: "btn-cta-how-it-works" }}
      />
    </main>
  );
}
